import {
  HttpRequestEntity,
  HttpRequestModelType,
  HttpRequestStatus,
} from 'shared/domain/httpRequest/httpRequestModel';
import { IHttpRequestRunner } from 'shared/domain/httpRequest/httpRequestRunner';
import type {
  AbortUploadDetail,
  FileServicesUnion,
} from 'shared/domain/messages/httpQueue/eventMessages';
import { swSignals } from 'serviceWorker/events/swSignals';
import { isUploadOf, toUploadEntityType } from './abortedUpload';
import { hardRemoveDocument } from 'serviceWorker/repository/document/hardRemoveDocument';
import { hardRemoveDocumentation } from 'serviceWorker/repository/documentation/hardRemoveDocumentation';
import { isAbortError } from 'serviceWorker/repository/httpRequest/abortRequest';
import { Services } from 'shared/domain/messages/message';
import * as httpRequests from 'serviceWorker/db/httpRequests';
import { HttpError, isRateLimitError } from 'serviceWorker/helpers/httpError';
import {
  CONFLICT,
  FORBIDDEN,
  NOT_FOUND,
  UNPROCESSABLE_ENTITY,
} from 'shared/contants';
import { isAwaitingApprovalsError, isNetworkFailureError, isStageRejectedError } from 'shared/utils/errorHandling';
import { swLog } from 'serviceWorker/helpers/makeSwLogger';
import { debugLog } from 'shared/logger/debugLog';
import { LogLevel } from 'shared/types/logger';
import { ensureAuthenticatedConfig } from '../config';
import {
  scheduleAfterAuthCooldown,
  scheduleAfterRateLimit,
} from 'serviceWorker/helpers/scheduleSwRetry';
import { emitErrorOnProperChannel } from './errorHandlers';
import {
  handleNonRetryableDocumentUploadError,
  removeRefusedFileCreation,
  removeOvertakenDocumentationChange,
} from './errorHandlers/documentation';
import { forgetNetworkLoss, retryAfterNetworkLoss } from './networkLossRetry';
import { httpRequestRunner } from './httpRequestRunner';
import { emitResponseOnProperChannel } from './responseHandlers';

let requestInProgress = false;

export function isHttpRequestInProgress(): boolean {
  return requestInProgress;
}

const executedUniqueIds = new Map<string, any>();

/** Runs the queue again unless it runs already (a request queued meanwhile started it). */
function resumeQueue(): void {
  if (!requestInProgress) {
    executeNextRequest(httpRequestRunner);
  }
}

function handleSetupUnavailable(request: HttpRequestEntity): void {
  debugLog('setup fail', request);
  requestInProgress = false;
  scheduleAfterAuthCooldown('http-queue', resumeQueue);
}

function handleRateLimitedRequest(): void {
  requestInProgress = false;
  scheduleAfterRateLimit('http-queue', resumeQueue);
}

/** No network: the queue stops and tries again later, keeping the request as it is. */
function handleNetworkLoss(): void {
  requestInProgress = false;
  retryAfterNetworkLoss(resumeQueue);
}

/** What the queue does after a request failed: stop until a scheduled retry, or go on past it. */
type AfterFailedRequest = Readonly<{ stop: true }> | Readonly<{ stop: false; skip: number }>;

const STOP_QUEUE: AfterFailedRequest = { stop: true };

/**
 * Settles a failed request: waits out a rate limit or a lost network, drops
 * what will never succeed, and marks the rest failed (`skip` counts the failed
 * request the retry of failed requests passes by).
 */
async function settleFailedRequest(
  request: HttpRequestEntity,
  error: unknown,
  uniqueId: string | undefined
): Promise<AfterFailedRequest> {
  if (isRateLimitError(error)) {
    handleRateLimitedRequest();
    return STOP_QUEUE;
  }
  if (await handleNonRetryableDocumentUploadError(httpRequests, request, error)) {
    return { stop: false, skip: 0 };
  }
  emitErrorOnProperChannel(request, error, uniqueId);
  if (isNetworkFailureError(error)) {
    handleNetworkLoss();
    return STOP_QUEUE;
  }
  return { stop: false, skip: await resolveSkipOrRemove(request, error) };
}

async function executeNextRequest(
  httpRequestRunner: IHttpRequestRunner
): Promise<void> {
  requestInProgress = true;
  debugLog('executeNextRequest');
  const request: HttpRequestEntity | undefined = await httpRequests
    .getFirst()
    .catch((e) => {
      requestInProgress = false;
      throw e;
    });
  debugLog('request', request);
  if (!request) {
    debugLog('request undefined', request);
    return executeNextFailedRequest(httpRequestRunner);
  }
  const setup = await ensureAuthenticatedConfig();
  if (!setup) {
    handleSetupUnavailable(request);
    return;
  }
  const uniqueId = request?.data?.uniqueId;
  try {
    if (uniqueId) {
      const existingResponse = executedUniqueIds.get(uniqueId);
      if (existingResponse) {
        // this happens when multiple tabs are open in the browser
        debugLog(
          'uniqueId already executed, skipping',
          uniqueId,
          request,
          existingResponse
        );

        executedUniqueIds.set(uniqueId, existingResponse);
        emitResponseOnProperChannel(request, existingResponse, uniqueId);
        await httpRequests.remove(request._id);

        return;
      }
    }

    const response = await httpRequestRunner.execute(request);
    debugLog('response', response);
    if (uniqueId) {
      executedUniqueIds.set(uniqueId, response);
      // in some places (eg. directories) id is used as uniqueId.
      // Until it's done with uniqueId, we have to clear this map because user can do another operation on the same entity
      setTimeout(() => {
        executedUniqueIds.delete(uniqueId);
      }, 5000);
    }

    forgetNetworkLoss();
    emitResponseOnProperChannel(request, response, uniqueId);

    await httpRequests.remove(request._id);
  } catch (e) {
    if ((await settleFailedRequest(request, e, uniqueId)).stop) {
      return;
    }
  }
  return executeNextRequest(httpRequestRunner);
}

async function executeNextFailedRequest(
  httpRequestRunner: IHttpRequestRunner,
  skip = 0
): Promise<void> {
  requestInProgress = true;
  debugLog('executeNextFailedRequest');
  const request: HttpRequestEntity | undefined =
    await httpRequests.getFirstFailed(skip);
  debugLog('failedRequest', request);
  if (!request) {
    const nextRequest = await httpRequests.getFirst();
    if (nextRequest) {
      return executeNextRequest(httpRequestRunner);
    }
    debugLog('failedRequest undefined', request);
    requestInProgress = false;
    return;
  }
  const setup = await ensureAuthenticatedConfig();
  if (!setup) {
    handleSetupUnavailable(request);
    return;
  }

  const uniqueId = request?.data?.uniqueId;

  try {
    const response = await httpRequestRunner.execute(request);
    debugLog('response', response);
    forgetNetworkLoss();

    // fire & forget
    emitResponseOnProperChannel(request, response, uniqueId);
    await httpRequests.remove(request._id);
  } catch (e) {
    const afterFailure = await settleFailedRequest(request, e, uniqueId);
    if (afterFailure.stop) {
      return;
    }
    skip += afterFailure.skip;
  }
  return executeNextFailedRequest(httpRequestRunner, skip);
}

export const init = (): void => {
  httpRequests.count().then((count) => {
    if (count > 0 && !requestInProgress) {
      debugLog('init queue', count, requestInProgress);
      return executeNextRequest(httpRequestRunner);
    }
  });

  swSignals.httpRequestQueued.subscribe(() => {
    if (!requestInProgress) {
      executeNextRequest(httpRequestRunner);
    }
  });

  swSignals.uploadAborted.subscribe(removeAbortedUpload);
};

/** Drops the queued upload of the file and the file itself. */
async function removeAbortedUpload(detail: AbortUploadDetail): Promise<void> {
  try {
    const requests = await httpRequests.getByQuery({
      method: 'POST',
      entityType: toUploadEntityType(detail.type),
    });
    const found = requests?.find((request) => isUploadOf(request, detail));
    if (!found) {
      return;
    }
    httpRequests.remove(found._id);
    await getRemoveHandler(detail.type)(detail.localId);
  } catch (e) {
    swLog(`Error on abort upload. ${(e as Error)?.message}`, LogLevel.INFO, e, null);
  }
}

function getRemoveHandler(
  type: FileServicesUnion
): (id: number) => Promise<unknown> {
  switch (type) {
    case Services.DOCUMENTATIONS: {
      return hardRemoveDocumentation;
    }
    case Services.DOCUMENTS: {
      return hardRemoveDocument;
    }
  }
}

async function resolveSkipOrRemove(
  request: HttpRequestEntity,
  e: unknown
): Promise<number> {
  if (isAbortError(e)) {
    return 0;
  }

  if (
    request.entityType === HttpRequestModelType.issue &&
    e instanceof HttpError &&
    (e.status === UNPROCESSABLE_ENTITY ||
      (e.status === CONFLICT && (isAwaitingApprovalsError(e) || isStageRejectedError(e))))
  ) {
    await httpRequests.remove(request._id);
    return 0;
  }

  // Workflow entities (decision/stage/workflow) hit the same conflict
  // semantics: STAGE_REJECTED / STAGE_AWAITING_APPROVALS responses are
  // permanent — retrying will keep failing — so drop them from the queue
  // to avoid an infinite retry loop after the user has been notified.
  if (
    (request.entityType === HttpRequestModelType.workflowDecision ||
      request.entityType === HttpRequestModelType.workflowStage ||
      request.entityType === HttpRequestModelType.workflowStageReorder ||
      request.entityType === HttpRequestModelType.workflow) &&
    e instanceof HttpError &&
    e.status === CONFLICT &&
    (isAwaitingApprovalsError(e) || isStageRejectedError(e))
  ) {
    await httpRequests.remove(request._id);
    return 0;
  }

  if (request.entityType === HttpRequestModelType.report) {
    await httpRequests.remove(request._id);
    return 0;
  }

  if (await removeOvertakenDocumentationChange(httpRequests, request, e)) {
    return 0;
  }

  if (
    await handleNonRetryableDocumentUploadError(
      httpRequests,
      request,
      e
    )
  ) {
    return 0;
  }

  if (
    e instanceof HttpError &&
    (e.status === FORBIDDEN || e.status === UNPROCESSABLE_ENTITY)
  ) {
    await httpRequests.remove(request._id);
    return 0;
  }

  // Any other failure of a file's creation is marked failed below like every request: kept new, it was tried again at once, for ever.
  if (
    (request.entityType === HttpRequestModelType.documentation ||
      request.entityType === HttpRequestModelType.document) &&
    request.method === 'POST' &&
    (await removeRefusedFileCreation(httpRequests, request, e))
  ) {
    return 0;
  }

  if (
    (request.entityType === HttpRequestModelType.level ||
      request.entityType === HttpRequestModelType.site) &&
    request.method === 'DELETE' &&
    e instanceof HttpError &&
    e.status === CONFLICT
  ) {
    await httpRequests.remove(request._id);
    return 0;
  }

  if (
    (request.entityType === HttpRequestModelType.level ||
      request.entityType === HttpRequestModelType.site ||
      request.entityType === HttpRequestModelType.company ||
      request.entityType === HttpRequestModelType.contract) &&
    e instanceof HttpError &&
    e.status === NOT_FOUND
  ) {
    await httpRequests.remove(request._id);
    return 0;
  }

  // protocolSection requests are always PUT (comment edits on an existing
  // section, see ProtocolSectionEditor) - a 404 means the section was removed
  // server-side (e.g. template restructuring), so the edit is permanently moot.
  if (
    (request.entityType === HttpRequestModelType.protocolSection ||
      request.entityType ===
        HttpRequestModelType.protocolSectionAnswersBatch) &&
    request.method === 'PUT' &&
    e instanceof HttpError &&
    e.status === NOT_FOUND
  ) {
    await httpRequests.remove(request._id);
    return 0;
  }

  await httpRequests.updateOne(request._id, {
    status: HttpRequestStatus.FAILED,
  });
  return 1;
}
